import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  Home, 
  User, 
  Car, 
  AlertCircle, 
  Calculator, 
  Check, 
  Clock,
  Wallet,
  FileText,
  DollarSign,
  Calendar,
  Percent
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';

// Loan type configurations
const loanConfigs = {
  payday: {
    name: 'Payday Loan',
    icon: Wallet,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    interestRate: 25,
    interestPeriod: 'monthly',
    minAmount: 200,
    maxAmount: 10000,
    minTerm: 1,
    maxTerm: 3,
    description: 'Quick cash until your next payday (25% monthly interest)'
  },
  personal: {
    name: 'Personal Loan',
    icon: User,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    interestRate: 18.5,
    minAmount: 1000,
    maxAmount: 300000,
    minTerm: 6,
    maxTerm: 60,
    description: 'Quick cash for any personal need'
  },
  home: {
    name: 'Home Loan',
    icon: Home,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    interestRate: 11.5,
    minAmount: 100000,
    maxAmount: 5000000,
    minTerm: 60,
    maxTerm: 360,
    description: 'Finance your dream home'
  },
  vehicle: {
    name: 'Car Finance',
    icon: Car,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    interestRate: 14.5,
    minAmount: 50000,
    maxAmount: 1500000,
    minTerm: 12,
    maxTerm: 84,
    description: 'Drive your new car today'
  }
};

// Standalone Calculator Component
const LoanCalculator = ({ loanType, config }) => {
  const [calcAmount, setCalcAmount] = useState(config.minAmount);
  const [calcTerm, setCalcTerm] = useState(config.minTerm);
  const [calcRate, setCalcRate] = useState(config.interestRate);
  
  const Icon = config.icon;

  const calculateMonthlyPayment = () => {
    const principal = calcAmount;
    const numPayments = calcTerm;
    
    if (principal <= 0 || calcRate <= 0 || numPayments <= 0) return 0;
    
    // For payday loans, interest is monthly (not annual)
    const monthlyRate = config.interestPeriod === 'monthly' 
      ? calcRate / 100 
      : calcRate / 100 / 12;
    
    const payment = (principal * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                    (Math.pow(1 + monthlyRate, numPayments) - 1);
    return payment;
  };

  const monthlyPayment = calculateMonthlyPayment();
  const totalRepayment = monthlyPayment * calcTerm;
  const totalInterest = totalRepayment - calcAmount;

  return (
    <Card className="card-raoul p-6 mb-6 border-2 border-dashed border-gray-200">
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center`}>
          <Calculator className={`w-6 h-6 ${config.color}`} />
        </div>
        <div>
          <h3 className="font-syne text-lg font-bold text-gray-900">{config.name} Calculator</h3>
          <p className="text-gray-500 text-sm">Calculate your monthly payments</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Calculator Inputs */}
        <div className="space-y-6">
          {/* Loan Amount Slider */}
          <div>
            <div className="flex justify-between mb-2">
              <Label className="text-gray-700">Loan Amount</Label>
              <span className="text-gray-900 font-bold">R{calcAmount.toLocaleString()}</span>
            </div>
            <Slider
              value={[calcAmount]}
              onValueChange={(value) => setCalcAmount(value[0])}
              min={config.minAmount}
              max={config.maxAmount}
              step={loanType === 'home' ? 50000 : (loanType === 'vehicle' ? 10000 : 1000)}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>R{config.minAmount.toLocaleString()}</span>
              <span>R{config.maxAmount.toLocaleString()}</span>
            </div>
          </div>

          {/* Term Slider */}
          <div>
            <div className="flex justify-between mb-2">
              <Label className="text-gray-700">Loan Term</Label>
              <span className="text-gray-900 font-bold">
                {loanType === 'home' ? `${Math.floor(calcTerm / 12)} years` : `${calcTerm} months`}
              </span>
            </div>
            <Slider
              value={[calcTerm]}
              onValueChange={(value) => setCalcTerm(value[0])}
              min={config.minTerm}
              max={config.maxTerm}
              step={loanType === 'home' ? 12 : 6}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{loanType === 'home' ? `${config.minTerm / 12} years` : `${config.minTerm} months`}</span>
              <span>{loanType === 'home' ? `${config.maxTerm / 12} years` : `${config.maxTerm} months`}</span>
            </div>
          </div>

          {/* Interest Rate */}
          <div>
            <div className="flex justify-between mb-2">
              <Label className="text-gray-700">Interest Rate</Label>
              <span className="text-gray-900 font-bold">{calcRate}%</span>
            </div>
            <Slider
              value={[calcRate]}
              onValueChange={(value) => setCalcRate(value[0])}
              min={5}
              max={25}
              step={0.5}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>5%</span>
              <span>25%</span>
            </div>
          </div>
        </div>

        {/* Calculator Results */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200">
          <h4 className="text-gray-500 text-sm mb-4">Your Estimated Payments</h4>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-afriperx-green/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-afriperx-gold" />
                </div>
                <span className="text-gray-600">Monthly Payment</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">R{monthlyPayment.toFixed(2)}</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-gray-600">Total Repayment</span>
              </div>
              <span className="text-xl font-bold text-gray-900">R{totalRepayment.toFixed(2)}</span>
            </div>

            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Percent className="w-5 h-5 text-yellow-600" />
                </div>
                <span className="text-gray-600">Total Interest</span>
              </div>
              <span className="text-xl font-bold text-orange-600">R{totalInterest.toFixed(2)}</span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Loan Amount</p>
                <p className="text-gray-900 font-medium">R{calcAmount.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500">Interest Rate</p>
                <p className="text-gray-900 font-medium">{calcRate}% p.a.</p>
              </div>
              <div>
                <p className="text-gray-500">Term</p>
                <p className="text-gray-900 font-medium">
                  {loanType === 'home' ? `${Math.floor(calcTerm / 12)} years (${calcTerm} months)` : `${calcTerm} months`}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Type</p>
                <p className="text-gray-900 font-medium">{config.name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export const EasyLoansSection = () => {
  const { user, api, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('payday');
  const [loans, setLoans] = useState([]);
  const [applying, setApplying] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    term: '12',
    purpose: '',
    propertyValue: '',
    depositAmount: '',
    vehicleType: '',
    vehicleYear: '',
    vehicleMake: ''
  });

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      const res = await api.get('/loans');
      setLoans(res.data);
    } catch (error) {
      console.error('Error fetching loans:', error);
    }
  };

  const config = loanConfigs[activeTab];
  const Icon = config.icon;

  const handleApply = async (e) => {
    e.preventDefault();
    
    if (user?.kyc_status !== 'approved') {
      toast.error('Please complete KYC verification before applying for a loan');
      return;
    }

    setApplying(true);
    try {
      const payload = {
        loan_type: activeTab,
        amount: parseFloat(formData.amount),
        term_months: parseInt(formData.term),
        purpose: formData.purpose || `${config.name} application`
      };

      if (activeTab === 'home') {
        payload.property_value = parseFloat(formData.propertyValue);
      }

      if (activeTab === 'vehicle') {
        payload.vehicle_details = JSON.stringify({
          type: formData.vehicleType,
          year: formData.vehicleYear,
          make: formData.vehicleMake
        });
      }

      const res = await api.post('/loans/apply', payload);
      toast.success(`${config.name} application submitted! Credit score: ${res.data.credit_score}`);
      fetchLoans();
      setFormData({
        amount: '',
        term: '12',
        purpose: '',
        propertyValue: '',
        depositAmount: '',
        vehicleType: '',
        vehicleYear: '',
        vehicleMake: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Application failed');
    } finally {
      setApplying(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      active: 'bg-blue-100 text-blue-800',
      completed: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  const myLoans = loans.filter(l => l.loan_type === activeTab);

  return (
    <div className="space-y-6" data-testid="easy-loans-section">
      <h1 className="font-syne text-2xl font-bold text-gray-900">Easy Loans</h1>

      {/* KYC Warning */}
      {user?.kyc_status !== 'approved' && (
        <Card className="card-raoul p-4 border-yellow-300 bg-yellow-50">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="text-gray-900 font-medium">KYC Verification Required</p>
              <p className="text-gray-500 text-sm">Complete your KYC verification to apply for any loan.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Loan Type Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 bg-gray-100 p-1 rounded-lg">
          {Object.entries(loanConfigs).map(([key, conf]) => {
            const TabIcon = conf.icon;
            return (
              <TabsTrigger 
                key={key} 
                value={key}
                className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                data-testid={`tab-${key}`}
              >
                <TabIcon className={`w-4 h-4 ${activeTab === key ? conf.color : 'text-gray-500'}`} />
                <span className="hidden sm:inline">{conf.name}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Loan Content */}
        <div className="mt-6">
          {/* Overview Card */}
          <Card className="card-raoul p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${config.color}`} />
              </div>
              <div>
                <h2 className="font-syne text-xl font-bold text-gray-900">{config.name}</h2>
                <p className="text-gray-500 text-sm">{config.description}</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-gray-500 text-xs">Interest Rate</p>
                <p className="text-gray-900 font-bold text-lg">{config.interestRate}%</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-gray-500 text-xs">Min Amount</p>
                <p className="text-gray-900 font-bold text-lg">{config.minAmount >= 1000 ? `R${(config.minAmount / 1000).toFixed(0)}k` : `R${config.minAmount}`}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-gray-500 text-xs">Max Amount</p>
                <p className="text-gray-900 font-bold text-lg">{config.maxAmount >= 1000000 ? `R${(config.maxAmount / 1000000).toFixed(1)}M` : config.maxAmount >= 1000 ? `R${(config.maxAmount / 1000).toFixed(0)}k` : `R${config.maxAmount}`}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-gray-500 text-xs">Max Term</p>
                <p className="text-gray-900 font-bold text-lg">
                  {activeTab === 'home' ? `${config.maxTerm / 12} yrs` : `${config.maxTerm} mo`}
                </p>
              </div>
            </div>
          </Card>

          {/* Loan Calculator */}
          <LoanCalculator loanType={activeTab} config={config} />

          {/* Application Form */}
          <Card className="card-raoul p-6 mb-6">
            <h3 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-afriperx-gold" />
              Apply for {config.name}
            </h3>
            
            <form onSubmit={handleApply} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600">Loan Amount (ZAR)</Label>
                  <Input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder={`${config.minAmount.toLocaleString()} - ${config.maxAmount.toLocaleString()}`}
                    required
                    min={config.minAmount}
                    max={config.maxAmount}
                    className="mt-2 bg-gray-50 border-gray-200 text-gray-900"
                    data-testid="loan-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-600">Loan Term</Label>
                  <Select value={formData.term} onValueChange={(v) => setFormData({ ...formData, term: v })}>
                    <SelectTrigger className="mt-2 bg-gray-50 border-gray-200 text-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {activeTab === 'home' ? (
                        <>
                          <SelectItem value="60">5 Years (60 months)</SelectItem>
                          <SelectItem value="120">10 Years (120 months)</SelectItem>
                          <SelectItem value="180">15 Years (180 months)</SelectItem>
                          <SelectItem value="240">20 Years (240 months)</SelectItem>
                          <SelectItem value="300">25 Years (300 months)</SelectItem>
                          <SelectItem value="360">30 Years (360 months)</SelectItem>
                        </>
                      ) : activeTab === 'vehicle' ? (
                        <>
                          <SelectItem value="12">12 Months</SelectItem>
                          <SelectItem value="24">24 Months</SelectItem>
                          <SelectItem value="36">36 Months</SelectItem>
                          <SelectItem value="48">48 Months</SelectItem>
                          <SelectItem value="60">60 Months</SelectItem>
                          <SelectItem value="72">72 Months</SelectItem>
                          <SelectItem value="84">84 Months</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="6">6 Months</SelectItem>
                          <SelectItem value="12">12 Months</SelectItem>
                          <SelectItem value="24">24 Months</SelectItem>
                          <SelectItem value="36">36 Months</SelectItem>
                          <SelectItem value="48">48 Months</SelectItem>
                          <SelectItem value="60">60 Months</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Home Loan specific fields */}
              {activeTab === 'home' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-600">Property Value (ZAR)</Label>
                    <Input
                      type="number"
                      value={formData.propertyValue}
                      onChange={(e) => setFormData({ ...formData, propertyValue: e.target.value })}
                      placeholder="1,500,000"
                      className="mt-2 bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Deposit Amount (ZAR)</Label>
                    <Input
                      type="number"
                      value={formData.depositAmount}
                      onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })}
                      placeholder="150,000"
                      className="mt-2 bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>
                </div>
              )}

              {/* Vehicle Finance specific fields */}
              {activeTab === 'vehicle' && (
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-600">Vehicle Type</Label>
                    <Select value={formData.vehicleType} onValueChange={(v) => setFormData({ ...formData, vehicleType: v })}>
                      <SelectTrigger className="mt-2 bg-gray-50 border-gray-200 text-gray-900">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="new">New Vehicle</SelectItem>
                        <SelectItem value="used">Used Vehicle</SelectItem>
                        <SelectItem value="demo">Demo Vehicle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-600">Vehicle Year</Label>
                    <Input
                      type="number"
                      value={formData.vehicleYear}
                      onChange={(e) => setFormData({ ...formData, vehicleYear: e.target.value })}
                      placeholder="2024"
                      className="mt-2 bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600">Make & Model</Label>
                    <Input
                      value={formData.vehicleMake}
                      onChange={(e) => setFormData({ ...formData, vehicleMake: e.target.value })}
                      placeholder="Toyota Corolla"
                      className="mt-2 bg-gray-50 border-gray-200 text-gray-900"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-gray-600">Purpose / Notes</Label>
                <Input
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder={activeTab === 'home' ? 'Property address or details' : 'What is this loan for?'}
                  className="mt-2 bg-gray-50 border-gray-200 text-gray-900"
                />
              </div>

              <Button
                type="submit"
                disabled={applying || user?.kyc_status !== 'approved'}
                className="btn-primary"
                data-testid="apply-loan-btn"
              >
                {applying ? 'Submitting...' : `Apply for ${config.name}`}
              </Button>
            </form>
          </Card>

          {/* My Loans */}
          {myLoans.length > 0 && (
            <Card className="card-raoul p-6">
              <h3 className="font-syne text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-500" />
                My {config.name}s
              </h3>
              <div className="space-y-4">
                {myLoans.map((loan) => (
                  <div key={loan.id} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${config.bgColor} flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${config.color}`} />
                        </div>
                        <div>
                          <p className="text-gray-900 font-bold text-xl">R{loan.amount?.toLocaleString()}</p>
                          <p className="text-gray-500 text-sm">{loan.term_months} months @ {loan.interest_rate}%</p>
                        </div>
                      </div>
                      {getStatusBadge(loan.status)}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Monthly Payment</p>
                        <p className="text-gray-900 font-medium">R{loan.monthly_payment?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Total Paid</p>
                        <p className="text-gray-900 font-medium">R{loan.total_paid?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Remaining</p>
                        <p className="text-gray-900 font-medium">R{loan.remaining_balance?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Credit Score</p>
                        <p className="text-gray-900 font-medium">{loan.credit_score}</p>
                      </div>
                    </div>

                    {/* Payment Schedule */}
                    {loan.payment_schedule && loan.payment_schedule.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-gray-700 font-medium text-sm mb-2">Payment Schedule</p>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {loan.payment_schedule.slice(0, 6).map((payment) => (
                            <div key={payment.payment_number} className="flex items-center justify-between text-sm bg-white rounded-lg p-2">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600">Payment #{payment.payment_number}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-gray-500">{payment.due_date?.split('T')[0]}</span>
                                <span className="text-gray-900 font-medium">R{payment.amount?.toFixed(2)}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  payment.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {payment.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </Tabs>
    </div>
  );
};
